
import React, { useEffect, useState, useRef } from "react";
import confetti from "canvas-confetti";

const TARGET = new Date("2026-12-11T07:00:00+03:00").getTime();
const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

// Phase constants
const PHASE_COUNTDOWN = 'countdown';
const PHASE_GRADUATION = 'graduation';
const PHASE_CHRISTMAS = 'christmas';
const PHASE_NEW_YEAR = 'new_year';
const PHASE_ARCHIVED = 'archived';

// Phase dates
const CHRISTMAS_2026 = new Date("2026-12-25T00:00:00+03:00").getTime();
const NEW_YEAR_2027 = new Date("2027-01-01T00:00:00+03:00").getTime();
const GRADUATION_END = TARGET + THREE_DAYS_MS;
const CHRISTMAS_END = CHRISTMAS_2026 + THREE_DAYS_MS;
const NEW_YEAR_END = NEW_YEAR_2027 + THREE_DAYS_MS;

// Zetech University color palette
const ZETECH_COLORS = [
  '#002147', // ZETECH BLUE
  '#000000', // RICH BLACK
  '#543D21', // BROWN
  '#8C92AC', // COOL GRAY
  '#A01705', // SIENNA
  '#3D8CD6', // SKY BLUE
  '#9E7369', // UMBER
  '#8A8542', // OLIVE
  '#E63C64', // CORAL
  '#D6C93D', // MUSTARD
];

function getRemaining() {
  const diff = Math.max(0, TARGET - Date.now());
  const seconds = Math.floor(diff / 1000);

  return {
    days: Math.floor(seconds / 86400),
    hours: Math.floor((seconds % 86400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: seconds % 60,
    complete: diff === 0,
  };
}

function getCurrentPhase() {
  const now = Date.now();

  if (now < TARGET) return PHASE_COUNTDOWN;
  if (now >= TARGET && now < GRADUATION_END) return PHASE_GRADUATION;
  if (now >= GRADUATION_END && now < CHRISTMAS_2026) return PHASE_ARCHIVED;
  if (now >= CHRISTMAS_2026 && now < CHRISTMAS_END) return PHASE_CHRISTMAS;
  if (now >= CHRISTMAS_END && now < NEW_YEAR_2027) return PHASE_ARCHIVED;
  if (now >= NEW_YEAR_2027 && now < NEW_YEAR_END) return PHASE_NEW_YEAR;
  return PHASE_ARCHIVED;
}

export default function ZetechCountdown() {
  const [time, setTime] = useState(getRemaining);
  const [phase, setPhase] = useState(getCurrentPhase);
  const celebrationTriggered = useRef(false);

  // Epic confetti celebration
  const triggerEpicConfetti = () => {
    const duration = 5000;
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 45,
      spread: 360,
      ticks: 80,
      zIndex: 1000,
      colors: ZETECH_COLORS,
    };

    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 100 * (timeLeft / duration);

      // Confetti from all sides
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0, 1), y: Math.random() - 0.2 },
      });

      // Firework-style bursts
      if (Math.random() > 0.7) {
        confetti({
          ...defaults,
          particleCount: 50,
          spread: 100,
          startVelocity: 60,
          origin: { x: randomInRange(0.2, 0.8), y: 0.5 },
        });
      }
    }, 200);
  };

  // Subtle continuous confetti
  const startSubtleConfetti = () => {
    const duration = 15000;
    const animationEnd = Date.now() + duration;
    const defaults = {
      startVelocity: 10,
      spread: 70,
      ticks: 150,
      zIndex: 999,
      colors: ZETECH_COLORS,
      particleCount: 2,
    };

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      confetti({
        ...defaults,
        origin: { x: Math.random(), y: -0.1 },
      });
    }, 300);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const newPhase = getCurrentPhase();
      setPhase(newPhase);

      if (phase === PHASE_COUNTDOWN) {
        setTime(getRemaining());
      }

      // Trigger epic celebration ONLY when graduation phase starts (on graduation day)
      if (newPhase === PHASE_GRADUATION && !celebrationTriggered.current) {
        celebrationTriggered.current = true;
        setTimeout(() => triggerEpicConfetti(), 500);
        setTimeout(() => startSubtleConfetti(), 1000);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [phase]);

  const getPhaseContent = () => {
    switch (phase) {
      case PHASE_COUNTDOWN:
        return {
          title: "Countdown to the 10th Graduation Ceremony 2026",
          subtitle: "Every second brings us closer.",
          showTimer: true,
          showConfettiHint: false
        };
      case PHASE_GRADUATION:
        return {
          title: "🎉 CONGRATULATIONS ON YOUR GRADUATION! 🎉",
          subtitle: "You made it! Celebrate this incredible achievement with pride.",
          showTimer: false,
          showConfettiHint: true,
          isEpic: true
        };
      case PHASE_CHRISTMAS:
        return {
          title: "🎄 MERRY CHRISTMAS! 🎄",
          subtitle: "Wishing you joy, peace, and happiness this holiday season.",
          showTimer: false,
          showConfettiHint: false
        };
      case PHASE_NEW_YEAR:
        return {
          title: "🎊 HAPPY NEW YEAR 2027! 🎊",
          subtitle: "New beginnings, new opportunities, new achievements await!",
          showTimer: false,
          showConfettiHint: false
        };
      case PHASE_ARCHIVED:
        return {
          title: "10th Graduation Ceremony 2026",
          subtitle: "This event has concluded. Check back for future ceremonies.",
          showTimer: false,
          showConfettiHint: false,
          isArchived: true
        };
      default:
        return {
          title: "10th Graduation Ceremony 2026",
          subtitle: "This event has concluded.",
          showTimer: false,
          showConfettiHint: false
        };
    }
  };

  const content = getPhaseContent();
  const units = [
    ["DAYS", time.days],
    ["HOURS", time.hours],
    ["MINUTES", time.minutes],
    ["SECONDS", time.seconds],
  ];

  return (
    <section
      aria-label="Graduation countdown and celebration"
      onClick={() => phase === PHASE_GRADUATION && triggerEpicConfetti()}
      style={{
        background: content.isEpic
          ? "linear-gradient(135deg, #FFD700, #FFA500, #FF6347)"
          : content.isArchived
          ? "linear-gradient(135deg, #4a5568, #64748b)"
          : phase === PHASE_CHRISTMAS
          ? "linear-gradient(135deg, #c41e3a, #e63946, #f1f405)"
          : phase === PHASE_NEW_YEAR
          ? "linear-gradient(135deg, #00ff87, #60efff, #00a8ff)"
          : "linear-gradient(135deg, #081C46, #153467)",
        color: content.isEpic ? "#002147" : "#fff",
        padding: "48px 20px",
        borderRadius: "20px",
        textAlign: "center",
        fontFamily: "Montserrat, sans-serif",
        cursor: phase === PHASE_GRADUATION ? "pointer" : "default",
        position: "relative",
        overflow: "hidden",
        transition: "background 1s ease",
      }}
    >
      <p style={{
        color: content.isEpic ? "#002147" : "#F5C542",
        fontWeight: 700,
        fontSize: content.isEpic ? "16px" : "14px"
      }}>
        ZETECH UNIVERSITY
      </p>

      {content.showConfettiHint && (
        <p style={{ fontSize: "12px", opacity: 0.6, marginBottom: "8px" }}>
          ✨ Click anywhere to celebrate! ✨
        </p>
      )}

      <h2 style={{ 
        fontSize: content.isEpic ? "clamp(28px, 5vw, 42px)" : "clamp(22px, 4vw, 36px)",
        color: content.isEpic ? "#002147" : "#fff"
      }}>
        {content.title}
      </h2>

      <p style={{ 
        fontSize: content.isEpic ? "18px" : "16px",
        opacity: content.isEpic ? 1 : 0.85,
        fontWeight: content.isEpic ? 600 : 400
      }}>
        {content.subtitle}
      </p>

      {content.showTimer && !time.complete && (
        <div
          role="timer"
          aria-live="off"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: "clamp(6px, 2vw, 20px)",
            maxWidth: "760px",
            margin: "32px auto",
          }}
        >
          {units.map(([label, value]) => (
            <div
              key={label}
              style={{
                background: content.isEpic ? "#FFFFFF" : "#153467",
                border: content.isEpic ? "2px solid #FFD700" : "1px solid #47648E",
                borderRadius: "12px",
                padding: "clamp(12px, 3vw, 24px) 4px",
                minWidth: 0,
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = content.isEpic 
                  ? "0 8px 20px rgba(255,215,0,0.5)" 
                  : "0 8px 20px rgba(245,197,66,0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div
                style={{
                  fontSize: "clamp(24px, 5vw, 56px)",
                  fontWeight: 800,
                  color: content.isEpic ? "#FF6347" : "#F5C542",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {String(value).padStart(2, "0")}
              </div>
              <div
                style={{
                  fontSize: "clamp(9px, 1.5vw, 13px)",
                  letterSpacing: "0.04em",
                  color: content.isEpic ? "#002147" : "#fff",
                }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>
      )}

      {content.isEpic && (
        <div style={{
          fontSize: "clamp(14px, 2vw, 18px)",
          margin: "24px 0",
          fontWeight: 600,
          animation: "pulse 2s ease-in-out infinite"
        }}>
          🎓 Congratulations, Graduates! 🎓
        </div>
      )}

      <p style={{ fontSize: "13px", opacity: 0.85, color: content.isEpic ? "#002147" : "#fff" }}>
        FRIDAY • 11 DECEMBER 2026 • 7:00 AM EAT . VENUE: Zetech University Technology Park Mang'u Campus, Kenya
      </p>
      <br />
      <p style={{ fontSize: "13px", opacity: 0.85, color: content.isEpic ? "#002147" : "#fff" }}>
        <a 
          href="https://bit.ly/4C0oEXJ" 
          style={{ 
            color: content.isEpic ? "#FF6347" : "#F5C542", 
            textDecoration: "underline",
            fontWeight: 600
          }}
        >
          CLEARANCE AND GRADUATION PORTAL.
        </a>
      </p>

      <style jsx>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </section>
  );
}
